import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Icon } from '@/shared/icons';
import { KanbanColumnProps } from './types';
import { useKanbanDensity, useDensityTokens } from './KanbanDensityContext';

/** Chave da linha-sentinela de paginação — nunca colide com id de documento. */
const FOOTER_ROW_KEY = '__kanban_footer__';

/**
 * Chave de medida da linha virtual. Sem isto o virtual-core cai no default
 * `(index) => index` e o cache de altura fica preso à POSIÇÃO: depois de arrastar
 * um card, o índice N herda a altura do ocupante anterior.
 */
function getRowKey(item: unknown, index: number): string | number {
  if (item !== null && typeof item === 'object' && 'id' in item) {
    const raw = (item as { id?: unknown }).id;
    if (typeof raw === 'string' || typeof raw === 'number') return raw;
  }
  return index;
}

/**
 * KanbanColumn Component
 *
 * Renders a single column in the Kanban board with header and cards.
 *
 * @template T - Type of items in the column
 */
export function KanbanColumn<T>({
  id,
  title,
  color,
  count,
  items,
  renderCard,
  onCardClick,
  enableDragDrop = false,
  onCardMove,
  emptyMessage = 'Nenhum item',
  totalValue,
  onImport,
  onExport,
  onShowInfo,
  showTotalValue = false,
  hasMore,
  loadingMore,
  loadMoreError,
  onLoadMore,
  enableReorder,
  onCardDrop,
  onIntraColumnDropBlocked,
  onAddCard,
  addCardLabel = 'Adicionar',
  sumLabel,
  fluid = false,
  breakdown,
  bare = false,
  laneSummary,
  battery
}: KanbanColumnProps<T>) {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [showInfoDropdown, setShowInfoDropdown] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [tooltip, setTooltip] = useState<{ label: string; top: number; left: number } | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  // Light dismiss (DS §Overlays dismissal): menus transientes fecham por Esc
  useEffect(() => {
    if (!showMenu && !showInfoDropdown) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setShowMenu(false); setShowInfoDropdown(false); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showMenu, showInfoDropdown]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const density = useKanbanDensity();
  const tokens = useDensityTokens();

  // Linha extra no fim quando há paginação: sentinel de load-more / spinner / retry.
  const hasFooterRow = Boolean(hasMore || loadingMore || loadMoreError);
  const rowCount = items.length + (hasFooterRow ? 1 : 0);

  // `getItemKey` entra nas dependências do memo interno do virtual-core, então
  // precisa de identidade estável: lê os itens por ref em vez de fechar sobre o
  // array (que troca de referência a cada refetch/evento de socket).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const getItemKey = useCallback(
    (index: number) => {
      const current = itemsRef.current;
      return index >= current.length ? FOOTER_ROW_KEY : getRowKey(current[index], index);
    },
    []
  );

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    // A linha-sentinela do rodapé mede ~40px e NÃO acompanha a densidade do card;
    // estimá-la em 150 (como antes) inflava o total da coluna em ~110px.
    estimateSize: (index) =>
      index >= items.length ? tokens.footerEstimate : tokens.estimateSize,
    overscan: tokens.overscan,
    getItemKey,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Dispara onLoadMore quando o sentinel (última linha) entra na viewport.
  useEffect(() => {
    if (!hasMore || loadingMore || loadMoreError || !onLoadMore) return;
    const last = virtualItems[virtualItems.length - 1];
    if (last && last.index === items.length) {
      onLoadMore(id);
    }
  }, [virtualItems, hasMore, loadingMore, loadMoreError, onLoadMore, id, items.length]);

  // Remedição na troca de densidade.
  //
  // 1) Trocar `estimateSize` NÃO invalida o cache de medidas do virtual-core: a
  //    função de estimativa não entra nas dependências do memo interno, e o
  //    `itemSizeCache` só é limpo por `enabled: false`, por mudança de `lanes` ou
  //    por `measure()` explícito. Sem esta chamada, as linhas montadas se corrigem
  //    um tick depois (com deriva de rolagem) e as linhas ainda não montadas
  //    guardam a altura da densidade anterior indefinidamente.
  //
  // 2) É `useLayoutEffect` de propósito: o adaptador React instala as opções novas
  //    DURANTE o render (`instance.setOptions` no corpo de `useVirtualizer`), então
  //    aqui a estimativa nova já vale e não há pintura com posições velhas.
  //
  // 3) Âncora de rolagem: quem reposiciona de fato NÃO é a chamada síncrona abaixo.
  //    `measure()` limpa o `itemSizeCache` mas não recalcula o `measurementsCache`,
  //    e é dele que `getOffsetForIndex` lê — então, no instante do `scrollToIndex`,
  //    o alvo é computado sobre as medidas VELHAS e o deslocamento aplicado é
  //    praticamente o mesmo em que já estamos (na prática, um no-op). O que importa
  //    é o efeito colateral: `scrollToIndex` planta `scrollState = { index, align }`
  //    e agenda `scheduleScrollReconcile()`; no rAF seguinte o `reconcileScroll()`
  //    do core (≥ 3.17) recomputa o alvo com o `measurementsCache` já refeito e
  //    corrige o offset. Como isso é API interna do core, reaplicamos a âncora por
  //    conta própria em um rAF nosso — aí as medidas novas já existem, e a âncora
  //    sobrevive caso o core mude esse mecanismo.
  const firstDensityRunRef = useRef(true);
  useLayoutEffect(() => {
    if (firstDensityRunRef.current) {
      firstDensityRunRef.current = false;
      return;
    }
    const anchorIndex = virtualizer.range?.startIndex ?? 0;
    virtualizer.measure();
    // Índice 0 também ancora: quem está rolado DENTRO do primeiro card precisa
    // voltar ao topo dele. Para quem já está em `scrollTop === 0` é um no-op.
    if (anchorIndex >= 0) {
      virtualizer.scrollToIndex(anchorIndex, { align: 'start' });
    }
    const rafId = requestAnimationFrame(() => {
      virtualizer.scrollToIndex(anchorIndex, { align: 'start' });
    });
    return () => cancelAnimationFrame(rafId);
  }, [density, virtualizer]);

  const showTooltip = (e: React.MouseEvent, label: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      label,
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  };

  const hideTooltip = () => setTooltip(null);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, item: T, itemIndex: number) => {
    if (!enableDragDrop) return;
    // Store the item data and source column id in the dataTransfer
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({
      item,
      fromColumnId: id,
      itemIndex
    }));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enableDragDrop) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    if (!enableDragDrop) return;
    setIsDraggingOver(false);
    setDropIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enableDragDrop) return;
    e.preventDefault();
    setIsDraggingOver(false);
    const insertionIndex = dropIndex ?? items.length;
    setDropIndex(null);

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const { item, fromColumnId } = data;

      if (enableReorder && onCardDrop) {
        onCardDrop(item, fromColumnId, id, { index: insertionIndex });
        return;
      }
      if (fromColumnId !== id && onCardMove) {
        onCardMove(item, fromColumnId, id);
      } else if (fromColumnId === id) {
        onIntraColumnDropBlocked?.(id);
      }
    } catch (error) {
      console.error('Error processing drop:', error);
    }
  };

  // Total p/ a mini-barra proporcional (.mk-bar)
  const breakdownTotal = (breakdown ?? []).reduce((acc, b) => acc + b.count, 0);

  // Traço de 2px centrado na faixa vazia entre dois cards. Com gap 8 dá -5, que é
  // exatamente onde o indicador ficava antes — a fórmula preserva o desenho atual
  // em Normal e acompanha Detalhes (10 → -6) e Compacto (6 → -4).
  const dropLineOffset = -Math.round(tokens.gap / 2) - 1;

  const miniBar = breakdown && breakdownTotal > 0 && (
    <div
      className="flex w-full overflow-hidden rounded-full"
      style={{ gap: '2px', height: '6px', backgroundColor: 'var(--theme-surface-sunken)' }}
      data-testid={`kanban-column-bar-${id}`}
    >
      {breakdown.filter((b) => b.count > 0).map((b) => (
        <i
          key={b.label}
          style={{
            height: '100%',
            minWidth: '3px',
            flexGrow: b.count,
            backgroundColor: b.color,
          }}
        />
      ))}
    </div>
  );

  const legend = breakdown && breakdownTotal > 0 && (
    <div className="flex flex-wrap" style={{ gap: '4px 10px' }}>
      {breakdown.filter((b) => b.count > 0).map((b) => (
        <span
          key={b.label}
          className="inline-flex items-center gap-[5px] text-[11px] whitespace-nowrap"
          style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
        >
          <span className="w-1.5 h-1.5 rounded-full flex-none" style={{ backgroundColor: b.color }} />
          {b.count} {b.label}
        </span>
      ))}
    </div>
  );

  return (
    <div
      className={`flex flex-col flex-1 transition-colors ${bare ? '' : 'rounded-xl'}`}
      style={{
        backgroundColor: bare
          ? 'transparent'
          : isDraggingOver ? 'var(--theme-surface-selected)' : 'var(--theme-card-background)',
        border: bare ? 'none' : '1px solid var(--theme-card-border)',
        boxShadow: !bare && isDraggingOver ? `inset 0 0 0 2px ${color}` : 'none',
        minWidth: fluid ? undefined : '300px',
        maxWidth: fluid ? undefined : '300px'
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Modo bare (mobile .mob): só a linha-resumo (.m-summary) — sem cabeçalho */}
      {bare && showTotalValue && (
        <div className="flex-none flex flex-col gap-1.5 px-1 pb-2">
          <div
            className="text-xs font-medium truncate"
            style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
          >
            {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(totalValue || 0)}
            {sumLabel && (
              <span style={{ color: 'var(--theme-muted-text)' }}>
                {' '}· {count} {sumLabel}
              </span>
            )}
          </div>
          {miniBar}
        </div>
      )}

      {/* Column Header — `.mk-head` (panel, anatomia única desde F86: dot 8px +
          título + count-pill; linha-resumo/bateria discretas abaixo) */}
      {!bare && <div
        className="px-3 pt-3 pb-2.5 flex flex-col gap-2"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          color: 'var(--theme-card-text)',
          borderBottom: '1px solid var(--theme-card-border)',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px'
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {/* Accent dot da coluna */}
          <span
            className="flex-none rounded-full"
            style={{ width: '8px', height: '8px', backgroundColor: color }}
          />
          <h3
            className="flex-1 min-w-0 font-semibold text-sm truncate"
            style={{ letterSpacing: '-0.02em' }}
            title={title}
          >
            {title}
          </h3>
          {/* Count badge */}
          <span
            className="flex-none rounded-full font-semibold"
            style={{
              fontSize: '12px',
              padding: '1px 8px',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              backgroundColor: 'var(--theme-surface-sunken)',
              color: 'var(--theme-muted-text)'
            }}
          >
            {count}
          </span>
          {(onImport || onExport || onShowInfo) && (
            <div className="relative flex-none">
              {/* Menu "···" da coluna (mockup .mk-head — ações não ficam sempre visíveis) */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  hideTooltip();
                  setShowMenu((v) => !v);
                }}
                className="flex items-center justify-center rounded-md transition-colors flex-none"
                style={{ width: '28px', height: '28px', color: 'var(--theme-muted-text)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                  showTooltip(e, 'Ações da etapa');
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  hideTooltip();
                }}
                data-testid={`kanban-column-menu-${id}`}
                aria-haspopup="menu"
                aria-expanded={showMenu}
              >
                <Icon name="more-vertical" size={15} />
              </button>

              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div
                    className="absolute right-0 mt-1.5 w-52 rounded-xl shadow-lg z-20 p-1.5"
                    style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
                    role="menu"
                  >
                    {onShowInfo && (
                      <button
                        role="menuitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          setShowInfoDropdown(true);
                        }}
                        className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] font-medium text-left"
                        style={{ color: '#374151' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <Icon name="info" size={15} style={{ color: '#64748b' }} />
                        Informações da etapa
                      </button>
                    )}
                    {onImport && (
                      <button
                        role="menuitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          onImport(id);
                        }}
                        className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] font-medium text-left"
                        style={{ color: '#374151' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <Icon name="upload" size={15} style={{ color: '#64748b' }} />
                        Importar
                      </button>
                    )}
                    {onExport && (
                      <button
                        role="menuitem"
                        data-testid={`kanban-column-export-${id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          onExport(id);
                        }}
                        className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] font-medium text-left"
                        style={{ color: '#374151' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <Icon name="download" size={15} style={{ color: '#64748b' }} />
                        Exportar
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* Painel de ID da etapa (aberto pelo item "Informações") */}
              {showInfoDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowInfoDropdown(false)} />
                  <div
                    className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg z-20 p-3"
                    style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <Icon name="info" size={16} style={{ color: color, flexShrink: 0 }} />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold mb-1" style={{ color: '#1e293b' }}>
                          ID da Etapa (Para Integração)
                        </h4>
                        <p className="text-xs mb-2" style={{ color: '#64748b' }}>
                          Use este ID para integrar com a API pública:
                        </p>
                      </div>
                    </div>
                    <div
                      className="p-2 rounded text-xs font-mono break-all"
                      style={{ backgroundColor: '#f1f5f9', color: '#1e293b', border: '1px solid #e5e7eb' }}
                    >
                      {id}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {/* Summary line (.mk-sum) — "R$ X · N {sumLabel}" quando há valor total */}
        {showTotalValue && (
          <div
            className="text-xs font-medium truncate"
            style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
          >
            {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(totalValue || 0)}
            {sumLabel && (
              <span style={{ color: 'var(--theme-muted-text)' }}>
                {' '}· {count} {sumLabel}
              </span>
            )}
          </div>
        )}
        {/* Mini-barra (.mk-bar) + legenda (.mk-legend) por status */}
        {miniBar}
        {legend}
        {/* Linha-resumo (ex-lane, adaptada ao panel — F86): "SLA · 1 vencido" /
            "No prazo · X/X" / "Encerrados · N" — mesma família visual da legenda */}
        {laneSummary && (
          <div
            className="flex items-center justify-between text-[11px]"
            style={{ color: 'var(--theme-muted-text)' }}
            data-testid={`kanban-column-summary-${id}`}
          >
            <span>{laneSummary.label}</span>
            <span
              className="font-semibold"
              style={{
                color: laneSummary.bad ? 'var(--theme-error-color, #EF4444)' : 'var(--theme-card-text)',
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {laneSummary.value}
            </span>
          </div>
        )}
        {/* Bateria (ex-lane, adaptada ao panel — F86): 1 segmento por item da
            janela, mesma família visual da mini-barra (.mk-bar) */}
        {battery && battery.length > 0 && (
          <div
            className="flex w-full overflow-hidden rounded-full"
            style={{ gap: '2px', height: '6px' }}
            data-testid={`kanban-column-battery-${id}`}
          >
            {battery.map((c, i) => (
              <i key={i} className="flex-1" style={{ height: '100%', minWidth: '3px', backgroundColor: c }} />
            ))}
          </div>
        )}
      </div>}

      {/* Column Content — virtualizado (@tanstack/react-virtual) */}
      <div
        ref={scrollRef}
        data-testid={`kanban-column-scroll-${id}`}
        className="flex-1 overflow-y-auto min-h-[200px] p-2"
        // Passo ÚNICO entre cards. Fonte de verdade: tokens de densidade. Nenhum
        // card de kanban declara margem própria — ver LeadCard/TicketCard.
        style={{ '--kanban-card-gap': `${tokens.gap}px` } as React.CSSProperties}
      >
        {items.length === 0 && !hasFooterRow ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            {emptyMessage}
          </div>
        ) : (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {virtualItems.map((virtualRow) => {
              const isFooter = virtualRow.index === items.length;
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                    paddingBottom: 'var(--kanban-card-gap)', // passo por densidade (§ Tarefa 6)
                  }}
                >
                  {isFooter ? (
                    <div
                      className="flex items-center justify-center py-1 text-sm"
                      data-testid={`kanban-column-footer-${id}`}
                    >
                      {loadMoreError ? (
                        <button
                          data-testid={`kanban-column-retry-${id}`}
                          onClick={() => onLoadMore?.(id)}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                          style={{ color: 'var(--theme-primary)' }}
                        >
                          Tentar novamente
                        </button>
                      ) : loadingMore ? (
                        <span style={{ color: 'var(--theme-muted-text)' }}>Carregando…</span>
                      ) : (
                        /* `.tk-col__foot` do mockup attendance: dica estática 11px faint */
                        <span className="text-[11px]" style={{ color: 'var(--theme-card-muted, var(--theme-muted-text))' }}>
                          Role para carregar mais
                        </span>
                      )}
                    </div>
                  ) : (
                    (() => {
                      const item = items[virtualRow.index];
                      return (
                        <div
                          draggable={enableDragDrop}
                          onDragStart={(e) => handleDragStart(e, item, virtualRow.index)}
                          onDragOver={
                            enableReorder
                              ? (e) => {
                                  e.preventDefault();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const before = e.clientY < rect.top + rect.height / 2;
                                  setDropIndex(before ? virtualRow.index : virtualRow.index + 1);
                                }
                              : undefined
                          }
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('[data-tags-area]')) return;
                            onCardClick?.(item);
                          }}
                          className={enableDragDrop ? 'cursor-move' : 'cursor-pointer'}
                          style={{ position: 'relative' }}
                        >
                          {enableReorder && dropIndex === virtualRow.index && (
                            <div
                              data-testid="kanban-drop-indicator"
                              style={{ position: 'absolute', top: dropLineOffset, left: 4, right: 4, height: 2, backgroundColor: 'var(--theme-primary)', borderRadius: 1, pointerEvents: 'none' }}
                            />
                          )}
                          {renderCard(item)}
                          {enableReorder && dropIndex === virtualRow.index + 1 && virtualRow.index === items.length - 1 && (
                            <div
                              data-testid="kanban-drop-indicator"
                              style={{ position: 'absolute', bottom: dropLineOffset, left: 4, right: 4, height: 2, backgroundColor: 'var(--theme-primary)', borderRadius: 1, pointerEvents: 'none' }}
                            />
                          )}
                        </div>
                      );
                    })()
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer "＋ Adicionar" (.tk-col__foot do mockup) — cria item já nesta coluna.
          No mobile bare o FAB da bottom-nav cobre a criação. */}
      {onAddCard && !bare && (
        <button
          type="button"
          onClick={() => onAddCard(id)}
          className="flex-none flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium transition-colors rounded-b-xl"
          style={{
            color: 'var(--theme-muted-text)',
            borderTop: '1px solid var(--theme-card-border)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            e.currentTarget.style.color = 'var(--theme-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
          data-testid={`kanban-column-add-${id}`}
        >
          <Icon name="plus" size={15} />
          {addCardLabel}
        </button>
      )}

      {/* Portal tooltip - appears below buttons with arrow on top */}
      {tooltip && createPortal(
        <div
          style={{
            position: 'fixed',
            top: tooltip.top,
            left: tooltip.left,
            transform: 'translateX(-50%)',
            zIndex: 9999,
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Arrow pointing up */}
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid var(--theme-sidebar-active)',
            }}
          />
          {/* Tooltip body */}
          <div
            style={{
              backgroundColor: 'var(--theme-sidebar-active)',
              color: '#ffffff',
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: 500,
              borderRadius: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            {tooltip.label}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
